import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import useStore from '../../store'
import { fmtWeeks } from '../../lib/timeline'

export default function SurvivalFilter() {
  const has        = useStore(s => s.has)
  const goals      = useStore(s => s.goals)
  const updateGoal = useStore(s => s.updateGoal)
  const after      = useStore(s => s.afterMutation)
  const [open, setOpen] = useState(false)

  if (!has('survival_filter')) return null

  const committed = goals
    .filter(g => ['committed','active','paused'].includes(g.status))
    .sort((a, b) => (a.priority_rank ?? 999) - (b.priority_rank ?? 999))

  const top2   = committed.slice(0, 2)
  const rest   = committed.slice(2)

  const move = async (id, dir) => {
    const g    = committed.find(x => x.id === id)
    const rank = g?.priority_rank ?? committed.findIndex(x => x.id === id) + 1
    await updateGoal(id, { priority_rank: rank + dir })
    after()
  }

  return (
    <div className="border-b border-m-border">
      <button onClick={() => setOpen(o => !o)}
        className={`w-full flex items-center justify-between px-5 py-2 font-mono text-[10px] tracking-widest uppercase transition-colors ${
          open ? 'text-m-accent' : 'text-m-faint hover:text-m-subtle'
        }`}>
        <span className="flex items-center gap-2">
          <span className={`w-1.5 h-1.5 rounded-full ${open ? 'bg-m-accent' : 'bg-m-faint'}`} />
          Survival filter
        </span>
        <span>{open ? '↑' : '↓'}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-4 max-w-2xl mx-auto">
              <p className="font-mono text-[10px] text-m-faint mb-3">
                If you could only work on two goals, which survive?
              </p>

              {/* Top 2 */}
              {top2.map((g, i) => (
                <div key={g.id} className="card p-3 mb-2 flex items-center gap-3 border-m-accent/20">
                  <span className="font-mono text-xs text-m-accent w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm text-m-text truncate">{g.title}</p>
                    <p className="font-mono text-[10px] text-m-faint">{fmtWeeks(g.estimated_weeks)}{g.weekly_hours ? ` · ${g.weekly_hours}h/wk` : ''}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => move(g.id, -1)} className="font-mono text-xs text-m-faint hover:text-m-subtle px-1">↑</button>
                    <button onClick={() => move(g.id, +1)} className="font-mono text-xs text-m-faint hover:text-m-subtle px-1">↓</button>
                  </div>
                </div>
              ))}

              {/* Rest — dimmed */}
              {rest.length > 0 && (
                <div className="opacity-30 flex flex-col gap-1.5 mt-3">
                  <p className="label mb-1">Deprioritized</p>
                  {rest.map((g, i) => (
                    <div key={g.id} className="card p-2.5 flex items-center gap-3">
                      <span className="font-mono text-[10px] text-m-faint w-4">{i + 3}</span>
                      <p className="font-mono text-xs text-m-text flex-1 truncate">{g.title}</p>
                      <div className="flex gap-1">
                        <button onClick={() => move(g.id, -1)} className="font-mono text-xs text-m-faint hover:text-m-subtle px-1">↑</button>
                        <button onClick={() => move(g.id, +1)} className="font-mono text-xs text-m-faint hover:text-m-subtle px-1">↓</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {committed.length === 0 && (
                <p className="font-mono text-xs text-m-faint">No committed goals.</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
